/* eslint-disable lingui/no-unlocalized-strings */
// Apply Edge — Contact page. Port of design/source/public/contact.html.
// Chrome (NavBar/Footer) is owned by HomeLayout — do not render here.
import { t } from "@lingui/macro";
import { ArrowRightIcon, CheckIcon, GlobeIcon, MapPinIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { Link } from "react-router";

import { SEO } from "@/client/components/seo";

type TopicId = "support" | "other";
type Topic = { id: TopicId; label: string; email: string; note: string };

const TOPICS: readonly Topic[] = [
  {
    id: "support",
    label: "Support",
    email: "info@apply-edge.com",
    note: "Bugs, account issues, and questions about features.",
  },
  {
    id: "other",
    label: "Other",
    email: "info@apply-edge.com",
    note: "Billing, partnerships, media, security, and anything else.",
  },
];

export const ContactPage = () => {
  const [topicId, setTopicId] = useState<TopicId>("support");
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", subject: "", body: "" });

  const topic = TOPICS.find((x) => x.id === topicId) ?? TOPICS[0];

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = encodeURIComponent(form.subject || `Apply Edge — ${topic.label}`);
    const body = encodeURIComponent(`From: ${form.name} <${form.email}>\n\n${form.body}`);
    window.location.href = `mailto:${topic.email}?subject=${subject}&body=${body}`;
    setSent(true);
  };

  const reset = () => {
    setSent(false);
    setForm({ name: "", email: "", subject: "", body: "" });
  };

  return (
    <div className="min-h-screen delay-100 duration-500 animate-in fade-in slide-in-from-bottom-4 fill-mode-both">
      <SEO
        title={t`Contact — Apply Edge`}
        description={t`Get in touch with the Apply Edge team through info@apply-edge.com.`}
        canonical="/contact"
      />

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1280px] px-6 py-20">
          <div className="grid grid-cols-1 gap-16 md:grid-cols-[1fr_1fr]">
            {/* Left — copy + emails */}
            <div>
              <span className="text-xs font-medium text-muted-foreground">Contact</span>
              <h1 className="mt-4 font-serif text-[44px] font-semibold leading-[1.1] tracking-tight text-foreground">
                We read everything.
                <br />
                <span className="italic text-muted-foreground">Promise.</span>
              </h1>
              <p className="mt-6 max-w-[44ch] text-[15px] leading-[1.6] text-muted-foreground">
                Apply Edge is a small, focused team. There&apos;s no support tier system. We aim to
                reply within a few business days.
              </p>

              <div className="mt-10">
                <div className="text-xs font-medium text-muted-foreground">Direct email</div>
                <ul className="mt-4 divide-y divide-border rounded-xl border border-border bg-surface">
                  <li className="px-5 py-4">
                    <p className="text-[14px] leading-[1.65] text-foreground">
                      All inquiries →{" "}
                      <a
                        href="mailto:info@apply-edge.com"
                        className="font-mono text-[12px] text-primary hover:underline"
                      >
                        info@apply-edge.com
                      </a>{" "}
                      — we read every message.
                    </p>
                  </li>
                </ul>
              </div>

              <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
                <div className="rounded-xl border border-border bg-surface p-5 shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md">
                  <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                    <MapPinIcon size={13} /> HQ
                  </div>
                  <p className="mt-3 text-[13.5px] leading-[1.55] text-foreground">
                    Apply Edge (entity formation in progress)
                    <br />
                    Dubai, UAE (registered address pending)
                  </p>
                </div>
                <div className="rounded-xl border border-border bg-surface p-5 shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md">
                  <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                    <GlobeIcon size={13} /> Security disclosure
                  </div>
                  <p className="mt-3 text-[13.5px] leading-[1.55] text-foreground">
                    PGP key available on request — email info@apply-edge.com and reference
                    &quot;security disclosure&quot;.
                  </p>
                </div>
              </div>
            </div>

            {/* Right — form */}
            <div className="rounded-2xl border border-border bg-surface p-8 shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md">
              {sent ? (
                <div className="grid h-full place-items-center text-center">
                  <div>
                    <div className="mx-auto grid size-14 place-items-center rounded-full bg-primary/10 text-primary">
                      <CheckIcon size={22} weight="bold" />
                    </div>
                    <h2 className="mt-5 font-serif text-[24px] font-semibold tracking-tight text-foreground">
                      Message received
                    </h2>
                    <p className="mt-2 max-w-[34ch] text-[13.5px] text-muted-foreground">
                      We&apos;ll reply to{" "}
                      <span className="font-mono text-foreground">
                        {form.email || "your email"}
                      </span>{" "}
                      within a few business days.
                    </p>
                    <button
                      className="mt-6 inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-[12.5px] text-foreground hover:bg-muted"
                      onClick={reset}
                    >
                      Send another
                    </button>
                  </div>
                </div>
              ) : (
                <form className="space-y-5" onSubmit={submit}>
                  <div>
                    <h2 className="font-serif text-[22px] font-semibold tracking-tight text-foreground">
                      Send us a note
                    </h2>
                    <p className="mt-1 text-[13px] text-muted-foreground">
                      Or just email{" "}
                      <a className="text-primary hover:underline" href={"mailto:" + topic.email}>
                        {topic.email}
                      </a>{" "}
                      directly.
                    </p>
                  </div>

                  <div>
                    <label className="block text-[12px] font-medium text-foreground">Topic</label>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      {TOPICS.map((x) => {
                        const active = topicId === x.id;
                        return (
                          <button
                            key={x.id}
                            type="button"
                            aria-pressed={active}
                            className={
                              "rounded-lg border px-3 py-2 text-left text-[12.5px] transition " +
                              (active
                                ? "border-foreground bg-foreground text-background"
                                : "border-border bg-background text-foreground hover:bg-muted")
                            }
                            onClick={() => {
                              setTopicId(x.id);
                            }}
                          >
                            {x.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <label className="block">
                      <span className="text-[12px] font-medium text-foreground">Your name</span>
                      <input
                        required
                        value={form.name}
                        className="mt-1.5 h-11 w-full rounded-xl border border-border bg-background px-3.5 text-[13.5px] text-foreground placeholder:text-muted-foreground/70 focus:border-foreground/40 focus:outline-none focus:ring-2 focus:ring-foreground/10"
                        placeholder="Sarah Mitchell"
                        onChange={(e) => {
                          setForm((f) => ({ ...f, name: e.target.value }));
                        }}
                      />
                    </label>
                    <label className="block">
                      <span className="text-[12px] font-medium text-foreground">Email</span>
                      <input
                        required
                        type="email"
                        value={form.email}
                        className="mt-1.5 h-11 w-full rounded-xl border border-border bg-background px-3.5 text-[13.5px] text-foreground placeholder:text-muted-foreground/70 focus:border-foreground/40 focus:outline-none focus:ring-2 focus:ring-foreground/10"
                        placeholder="you@email.com"
                        onChange={(e) => {
                          setForm((f) => ({ ...f, email: e.target.value }));
                        }}
                      />
                    </label>
                  </div>

                  <label className="block">
                    <span className="text-[12px] font-medium text-foreground">Subject</span>
                    <input
                      required
                      value={form.subject}
                      className="mt-1.5 h-11 w-full rounded-xl border border-border bg-background px-3.5 text-[13.5px] text-foreground placeholder:text-muted-foreground/70 focus:border-foreground/40 focus:outline-none focus:ring-2 focus:ring-foreground/10"
                      placeholder="Quick question about Pro tier"
                      onChange={(e) => {
                        setForm((f) => ({ ...f, subject: e.target.value }));
                      }}
                    />
                  </label>

                  <label className="block">
                    <span className="text-[12px] font-medium text-foreground">Message</span>
                    <textarea
                      required
                      rows={5}
                      value={form.body}
                      className="mt-1.5 w-full rounded-xl border border-border bg-background px-3.5 py-2.5 text-[13.5px] text-foreground placeholder:text-muted-foreground/70 focus:border-foreground/40 focus:outline-none focus:ring-2 focus:ring-foreground/10"
                      placeholder="Tell us what's up…"
                      onChange={(e) => {
                        setForm((f) => ({ ...f, body: e.target.value }));
                      }}
                    />
                  </label>

                  <button
                    type="submit"
                    className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-primary text-[13.5px] font-medium text-primary-foreground shadow-[0_6px_20px_-6px_hsl(var(--primary)/0.45)] hover:opacity-90"
                  >
                    Send message
                    <ArrowRightIcon size={14} />
                  </button>
                  <p className="text-center text-[11.5px] text-muted-foreground">
                    By sending, you agree to our{" "}
                    {/* [QA-M] /privacy-policy is a 404 — canonical route is /privacy */}
                    <Link to="/privacy" className="hover:underline">
                      privacy policy
                    </Link>
                    .
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
